<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { page } from '$app/stores';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { trackButtonClick } from '$lib/global/tracking';
    import { getColor, getColorByLightness, personalizeText } from '$lib/helper';
    import { advancedLinking } from '$lib/Stores/stores';
    import { handleLinkingNavigation, getLinkingPreloadTarget } from '$lib/utils/linkingNavigation';
    import { getAllUserAnswers } from '$lib/utils/userAnswerStorage';

    import get from 'lodash/get';

    import ButtonView from './Button.View.svelte';

    import type { ButtonContent } from '$lib/types/Components/Button/ButtonContent';
    import type { Language } from '$lib/types/Language';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';

    export let id = '';
    export let content: ButtonContent;
    export let theme: Theme;
    export let nextPage = '';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { pageIdSlugMap },
    } = $page;

    const personalizedText = personalizeText(
        content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );

    const originalLogicNextPage: string = get(content, 'linking.pageId', nextPage);
    const linkingComponentId = get(content, 'linking.componentId');
    const externalLink: string = get(content, 'linking.url', '');
    const isNewTab: boolean = get(content, 'linking.isNewTab', false);
    const bgColor = getColor(content.backgroundColor, theme.buttonBackgroundColor);
    const color = getColor(content.color, getColorByLightness(bgColor));

    const nextPageNavigator = new NextPageNavigator(
        campaignId,
        firstResultPage,
        pageIdSlugMap,
        trackingProperties,
    );

    const handleClick = async () => {
        // Tracking
        trackButtonClick(id, content);

        return handleLinkingNavigation({
            componentId: id,
            userAnswers: getAllUserAnswers(campaignVersionId),
            advancedLinking: $advancedLinking,
            nextPageNavigator,
            originalLogicNextPage,
            nextPage,
            externalLink,
            isExternal: !content.linking.isInternal && !!externalLink,
            isNewTab,
            currentPageId: $page.data.pageId,
            linkingComponentId,
        });
    };

    const handleMouseEnter = () => {
        const { pageId, isExternal } = getLinkingPreloadTarget({
            componentId: id,
            userAnswers: getAllUserAnswers(campaignVersionId),
            advancedLinking: $advancedLinking,
            nextPageNavigator,
            originalLogicNextPage,
            nextPage,
            isExternal: !content.linking.isInternal && !!externalLink,
        });

        nextPageNavigator.preload(pageId, isExternal);
    };
</script>

<ButtonView
    draft={personalizedText}
    box={content.box}
    size={content.size}
    align={content.align}
    onClick={handleClick}
    onMouseEnter={handleMouseEnter}
    animation={content.animation}
    buttonStyle={content.buttonStyle}
    fullWidthOnDesktop={content.fullWidthOnDesktop}
    {transitionDelay}
    {transitionEffect}
    {bgColor}
    {color}
    {id}
    {layout}
    {language}
/>
